import React, { useState, useRef } from "react";
import Modal from "react-modal";

if (typeof document !== "undefined") {
  try {
    Modal.setAppElement("#root");
  } catch {}
}

const API_BASE =
  process.env.REACT_APP_PINNACLE_BASE_URL ||
  "https://onlineexam.ssccglpinnacle.com";

const SITE_ORIGIN =
  process.env.REACT_APP_PUBLIC_SITE_ORIGIN ||
  (typeof window !== "undefined" ? window.location.origin : "");

const isLocalhost =
  typeof window !== "undefined" &&
  /^localhost(:\d+)?$/i.test(window.location.hostname);

const PATH_MAP = {
  "typing-pass": "/typingExamSelection",
};

const ShareModal = ({
  product,
  stdcpncde = "",
  userEmail = "",
  shareUrl: shareUrlProp,
  shareText: shareTextProp,
  forceModal = false,
  debug = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [effectiveCoupon, setEffectiveCoupon] = useState(stdcpncde || "");
  const openingRef = useRef(false);

  const isMobile =
    typeof navigator !== "undefined" &&
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const baseWhatsApp = isMobile
    ? "https://api.whatsapp.com/send"
    : "https://web.whatsapp.com/send";

  const resolveBasePath = () => {
    if (shareUrlProp) return shareUrlProp;

    if (product?.slug && PATH_MAP[product.slug]) return PATH_MAP[product.slug];
    if (String(product?.id) === "999" || /typing/i.test(product?.slug || "")) {
      return "/typingExamSelection";
    }
    if (product?.slug) return `/buy/${product.slug}`;
    return "/";
  };

  const getShareUrl = (couponOverride) => {
    const code = (couponOverride ?? effectiveCoupon)?.trim();
    const u = new URL(resolveBasePath(), SITE_ORIGIN);
    u.searchParams.set("src", "share");
    if (product?.id) u.searchParams.set("pid", String(product.id));
    if (code) u.searchParams.set("stdcpncde", code);
    const finalUrl = u.toString();
    debug && console.log("[Share URL]", finalUrl);
    return finalUrl;
  };

  const shareMessage = (couponOverride) => {
    const url = getShareUrl(couponOverride);
    if (shareTextProp)
      return /\bhttps?:\/\//i.test(shareTextProp)
        ? shareTextProp
        : `${shareTextProp}\n${url}`;

    if (product?.title) {
      const lines = [`Check out this pass: ${product.title}`];
      if (product?.price != null) lines.push(`Price: ₹${product.price}`);
      if (product?.totalTests != null)
        lines.push(`Includes ${product.totalTests} tests.`);
      return `${lines.join("\n")}\n${url}`;
    }
    return `Check this typing module! 🚀 ${url}`;
  };

  const postShare = async (channel) => {
    if (!product?.id) {
      alert("Product ID missing.");
      return { ok: false };
    }

    const payload = {
      ...(isLocalhost && userEmail
        ? { email: String(userEmail).trim().toLowerCase() }
        : {}),
      stdcpncde: effectiveCoupon || "",
      cartItems: [
        {
          id: product.id,
          title: product.title,
          price: Number(product.price) || 0,
          //maxPrice: Number(product.maxPrice) || 0,
          totalTests: product.totalTests,
          slug: product.slug,
        },
      ],
      price: Number(product?.price) || 0,
      productId: Number(product.id),
      channel,
    };

    try {
      setBusy(true);
      const res = await fetch(`${API_BASE}/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        keepalive: true,
        body: JSON.stringify(payload),
      });
      let data = {};
      try {
        data = await res.json();
      } catch {}
      if (res.status === 401 || data?.error === "auth_required") {
        alert("Please login to share.");
        window.location.href = "https://testportal.ssccglpinnacle.com/login";
        return { ok: false };
      }

      if (!res.ok || data?.ok === false) {
        alert("Unable to log share. Please try again.");
        return { ok: false };
      }
      if (data?.stdcpncde) setEffectiveCoupon(data.stdcpncde);
      return { ok: true, data };
    } catch {
      alert("Network error while logging share.");
      return { ok: false };
    } finally {
      setBusy(false);
    }
  };

  const openModal = async () => {
    if (!forceModal && isMobile && navigator.share) {
      const r = await postShare("web-share");
      if (!r.ok) return;
      const code = r?.data?.stdcpncde || effectiveCoupon;
      try {
        await navigator.share({
          title: product?.title || "Share",
          text: shareMessage(code),
          url: getShareUrl(code),
        });
      } catch {}
      return;
    }
    setIsOpen(true);
  };

  const shareOnWhatsApp = async () => {
    if (openingRef.current) return;
    openingRef.current = true;
    const r = await postShare("whatsapp");
    if (!r.ok) {
      openingRef.current = false;
      return;
    }
    const code = r?.data?.stdcpncde || effectiveCoupon;
    window.open(
      `${baseWhatsApp}?text=${encodeURIComponent(shareMessage(code))}`,
      "_blank",
      "noopener,noreferrer"
    );
    setIsOpen(false);
    openingRef.current = false;
  };

  const shareOnTelegram = async () => {
    if (openingRef.current) return;
    openingRef.current = true;
    const r = await postShare("telegram");
    if (!r.ok) {
      openingRef.current = false;
      return;
    }
    const code = r?.data?.stdcpncde || effectiveCoupon;
    const text = shareMessage(code);
    window.open(
      `https://t.me/share/url?url=${encodeURIComponent(
        getShareUrl(code)
      )}&text=${encodeURIComponent(text)}`,
      "_blank",
      "noopener,noreferrer"
    );
    setIsOpen(false);
    openingRef.current = false;
  };

  const shareOnFacebook = async () => {
    if (openingRef.current) return;
    openingRef.current = true;
    const r = await postShare("facebook");
    if (!r.ok) {
      openingRef.current = false;
      return;
    }
    const code = r?.data?.stdcpncde || effectiveCoupon;
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        getShareUrl(code)
      )}`,
      "_blank",
      "noopener,noreferrer"
    );
    setIsOpen(false);
    openingRef.current = false;
  };

  const shareViaEmail = async () => {
    if (openingRef.current) return;
    openingRef.current = true;
    const r = await postShare("email");
    if (!r.ok) {
      openingRef.current = false;
      return;
    }
    const code = r?.data?.stdcpncde || effectiveCoupon;
    const subject = `Recommended: ${product?.title || "Typing Module"}`;
    const body = shareMessage(code);
    window.open(
      `https://mail.google.com/mail/?view=cm&fs=1&to=&su=${encodeURIComponent(
        subject
      )}&body=${encodeURIComponent(body)}`,
      "_blank",
      "noopener,noreferrer"
    );
    setIsOpen(false);
    openingRef.current = false;
  };

  return (
    <div>
      <style>
        {`
  .glow-button {
    position: relative;
    padding: 8px 18px;
    font-size: 13px;
    font-weight: 600;
    color: white;
    border-radius: 9999px;
    background: linear-gradient(135deg, #4facfe, #8a2be2);
    cursor: pointer;
    border: none;
    z-index: 1;
    overflow: hidden;
    transition: all 0.3s ease;
  }

  .glow-button:hover {
    background: linear-gradient(135deg, #00f2fe, #4facfe);
    transform: scale(1.05);
    box-shadow: 0 0 15px rgba(79, 172, 254, 0.7);
  }

  .glow-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .glow-button::before {
    content: "";
    position: absolute;
    inset: -2px;
    border-radius: 9999px;
    padding: 2px;
    background: conic-gradient(
      from 0deg,
      #00ffcc,
      #33ccff,
      #9966ff,
      #ff66cc,
      #00ffcc
    );
    -webkit-mask: 
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    animation: spin 3s linear infinite;
    z-index: -1;
  }

  @keyframes spin {
    100% {
      transform: rotate(360deg);
    }
  }
`}
      </style>

      <button
        className="glow-button"
        onClick={openModal}
        disabled={busy}
        title={busy ? "Saving..." : "Share"}
      >
        {busy ? "Saving..." : "Share & Earn"}
      </button>

      <Modal
        isOpen={isOpen}
        onRequestClose={() => setIsOpen(false)}
        contentLabel="Share Modal"
        style={{
          overlay: {
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            backdropFilter: "blur(4px)",
          },
          content: {
            inset: "50% auto auto 50%",
            transform: "translate(-50%, -50%)",
            background: "#fff",
            borderRadius: "16px",
            padding: "30px 40px",
            border: "none",
            boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "20px",
            minWidth: "280px",
            maxWidth: "660px",
          },
        }}
      >
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Share To</h3>

        <div style={{ display: "flex", gap: 40 }}>
          <CircleBtn
            label="WhatsApp"
            color="#25D366"
            onClick={shareOnWhatsApp}
          />
          <CircleBtn label="Email" color="#007bff" onClick={shareViaEmail} />
          <CircleBtn
            label="Telegram"
            color="#0088cc"
            onClick={shareOnTelegram}
          />

          <CircleBtn
            label="Facebook"
            color="#1877f2"
            onClick={shareOnFacebook}
          />
        </div>

        <button
          onClick={() => setIsOpen(false)}
          style={{
            marginTop: 8,
            color: "#e11d48",
            background: "transparent",
            border: "none",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          ✖ Close
        </button>
      </Modal>
    </div>
  );
};

function CircleBtn({ label, color, onClick }) {
  return (
    <div style={{ textAlign: "center" }}>
      <button
        onClick={onClick}
        style={{
          background: color,
          border: "none",
          borderRadius: "50%",
          width: 64,
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          boxShadow: `0 4px 12px ${color}66`,
        }}
        title={`Share via ${label}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="white"
          viewBox="0 0 24 24"
          width="28"
          height="28"
        >
          <path d="M20 4H4C2.35 4 1 5.35 1 7v10c0 1.65 1.35 3 3 3h16c1.65 0 3-1.35 3-3V7c0-1.65-1.35-3-3-3Zm0 2-8 5L4 6h16Z" />
        </svg>
      </button>
      <div style={{ marginTop: 8, fontSize: 14, fontWeight: 500 }}>{label}</div>
    </div>
  );
}

export default ShareModal;
